import { ButtonGroup, Horizontal, Vertical } from "@kea-mod/common";
import { KEACircle, KEAGraph, KEALink, KEAPaper } from "@kea-mod/jointjs";
import { IKEAGraphContext, InteractionType } from "context/KEAGraphContext";
import { getAuthorView, getFeedbackHighlightColor, getModelId, getSubmissionExerciseId } from "helper/Helper";
import * as joint from "jointjs";
import { MessageType } from "modals/SuccessModal";
import { Component } from "react";
import { useTranslation } from "react-i18next";
import { APIManager } from "service/APIManager";
import { FeedbackGraph } from "shared/FeedbackGraph";
import { FitModelButton } from "shared/buttons/FitModelButton";
import { ZoomInButton } from "shared/buttons/ZoomInButton";
import { ZoomOutButton } from "shared/buttons/ZoomOutButton";
import { ModelExportButton } from "shared/buttons/ModelExportButton";
import { FeedbackMessage, ValuationType } from "tasks/sample_feedback";

// Own component so it can use the translation hook, which the class below cannot.
const ModelLoadingOverlay = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        background: "rgba(255, 255, 255, 0.82)",
        // Purely informational, so it must not swallow clicks meant for the canvas.
        pointerEvents: "none",
      }}
    >
      <span
        className="button is-loading"
        style={{ border: "none", background: "transparent", boxShadow: "none", width: "2rem", height: "2rem" }}
      />
      <span className="has-text-grey">{t("modelLoading")}</span>
    </div>
  );
};

interface State {}

interface Props {
  width: number;
  height: number;
  modelingGraph: KEAGraph;
  modelingPaper: KEAPaper;
  feedback: { [id: string]: Array<FeedbackMessage> };
  unresolvedFeedback: Array<FeedbackMessage>;
  setNode: (node?: any) => void;
  toggleErrorModal: (errorMessageType?: MessageType) => void;
  updateFeedback: (id: string) => void;
  apiManager: APIManager;
  positionFeedback: Array<FeedbackMessage>;
  addUserInteraction: (
    interactionType: InteractionType,
    interactionTime: number,
    payload?: KEAGraph | joint.dia.Link | joint.dia.Cell,
  ) => void;
  setFeedback: (feedback: { [id: string]: Array<FeedbackMessage> }) => void;
  setUnresolvedFeedback: (feedback: Array<FeedbackMessage>) => void;
  setPositionFeedback: (feedback: Array<FeedbackMessage>) => void;
  keaGraphContext: IKEAGraphContext;
}

const PICK_HIGHLIGHT = "anchor-pick";
const SHOW_HIGHLIGHT = "shown-from-list";

export class ContentView extends Component<Props, State> {
  private pickedCellId: string | number | undefined;
  private shownCellId: string | number | undefined;

  componentDidMount = async () => {
    const submissionExerciseId = getSubmissionExerciseId();
    const modelId = getModelId();

    if (modelId) {
      this.props.keaGraphContext.setModelLoaded(false);
      this.props.apiManager.getModel(modelId).then((result: any) => {
        if (result.success === true) {
          const graph = result.data.data.graphData.graph as KEAGraph;
          this.props.keaGraphContext.loadModel(graph, () => {
            this.addMouseEnterAndMouseLeaveEvents();
            this.addElementPickEvents();
            this.addMessageEvents();
            Object.keys(this.props.feedback).forEach((id) => {
              this.props.updateFeedback(id);
            });
            // Correcting a submission starts by looking at the whole model, so it is
            // brought into view once. The callback runs only after the cells are in the
            // DOM, which means the paper exists and its size is known.
            this.props.modelingPaper.handleFit();
          });
        }
        //else handle error
      });
    }

    if (submissionExerciseId) {
      this.loadFeedback(submissionExerciseId, 2000);
    }
  };

  // Feedback is not only loaded once: the correction view says when an entry was added or
  // removed, otherwise the marks would keep showing a state that no longer exists.
  private loadFeedback = (submissionExerciseId: string, delay: number) => {
    {
      this.props.apiManager.getAssessmentsForSubmissionExercise(submissionExerciseId).then((result: any) => {
        setTimeout(() => {
          let feedbacks: { [id: string]: Array<FeedbackMessage> } = {};
          let unresolvedFeedbacks: Array<FeedbackMessage> = [];
          let positionFeedback: Array<FeedbackMessage> = [];

          result.forEach((item: FeedbackMessage) => {
            if (typeof item.position === "string") {
              if (!feedbacks[item.position]) {
                feedbacks[item.position] = [];
              }
              const newFeedack = feedbacks[item.position];
              newFeedack.push(item);
              feedbacks[item.position] = newFeedack;
            }
            if (typeof item.position === "object") {
              if (item.position.x && item.position.y) {
                positionFeedback.push(item);
              }
            }
            if (typeof item.position === "undefined") {
              unresolvedFeedbacks.push(item);
            }
          });
          this.props.setFeedback(feedbacks);
          this.props.setUnresolvedFeedback(unresolvedFeedbacks);
          this.props.setPositionFeedback(positionFeedback);
        }, delay);
      });
    }
  };

  componentDidUpdate = (prevProps: Props) => {
    if (
      this.props.feedback !== prevProps.feedback ||
      this.props.positionFeedback !== prevProps.positionFeedback ||
      this.props.unresolvedFeedback !== prevProps.unresolvedFeedback
    ) {
      this.unhighlightAllElements();
      this.highlightAllElements();
      this.drawNumberBadges();
      // The list may have changed while this view reloaded its own feedback.
      window.parent.postMessage({ type: "numbersPlease" }, "*");
    }
  };

  getHighlightMarkup = (valuationTypes: Array<FeedbackMessage>) => {
    if (valuationTypes.find((e) => e.valuation === ValuationType.NEGATIVE)) {
      return {
        stroke: "#FF4365",
        "stroke-width": 4,
      };
    }
    if (valuationTypes.find((e) => e.valuation === ValuationType.NEUTRAL)) {
      return {
        stroke: "#9C9998",
        "stroke-width": 4,
      };
    }
    if (valuationTypes.find((e) => e.valuation === ValuationType.OTHER)) {
      return {
        stroke: "#9C9998",
        "stroke-width": 4,
      };
    }
    if (valuationTypes.find((e) => e.valuation === ValuationType.POSITIVE)) {
      return {
        stroke: "#3FCE41",
        "stroke-width": 4,
      };
    }

    return {
      stroke: "#9C9998",
      "stroke-width": 4,
    };
  };

  createFeedbackInfoShapeAt = (position: joint.dia.Point, type: ValuationType): KEACircle => {
    const shape = new KEACircle();
    shape.position(position.x, position.y);
    shape.size(25, 25);
    shape.setLabel("i");

    const feedbackColor = getFeedbackHighlightColor(type);
    shape.setSecondaryColor(feedbackColor);
    shape.setPrimaryColor(feedbackColor);
    shape.setFillOpacity(0.3);
    return shape;
  };

  highlightElement = (id: string, feedbacks: Array<FeedbackMessage>) => {
    const cells = this.props.modelingGraph.getCells();
    const cell = cells.find((element) => element.id === id);
    if (!cell) return;

    const cellView = this.props.modelingPaper.findViewByModel(cell);
    if (!cellView) return;

    if (cell.get("type") === "kea.Link") {
      (cell as KEALink).highlight(this.getHighlightMarkup(feedbacks).stroke);
    } else {
      // The main shape is not called "body" on every element: a BPMN event has "outer",
      // a datastore "page". A fixed selector finds no node there and the mark never appears.
      const selector =
        typeof (cell as any).getConnectionSelector === "function" ? (cell as any).getConnectionSelector() : "body";
      joint.highlighters.mask.add(cellView as any, selector, "highlighter-selector", {
        attrs: this.getHighlightMarkup(feedbacks),
      });
    }
  };

  highlightAllElements = () => {
    for (const id in this.props.feedback) {
      this.highlightElement(id, this.props.feedback[id]);
    }
    this.props.positionFeedback.forEach((feedback) => {
      this.props.modelingGraph.addCell(
        this.createFeedbackInfoShapeAt(feedback.position as joint.dia.Point, feedback.valuation),
      );
    });
  };

  private clearShownElement = () => {
    if (!this.shownCellId) return;
    const id = String(this.shownCellId);
    const cell = this.props.modelingGraph.getCells().find((c) => String(c.id) === id);
    this.shownCellId = undefined;
    if (!cell) return;

    if (cell.get("type") === "kea.Link") {
      // A link carries the colour itself, so clearing it also drops its assessment colour.
      // Put that back instead of leaving the link plain.
      (cell as KEALink).unhighlight();
      const feedbacks = this.props.feedback[id];
      if (feedbacks) this.highlightElement(id, feedbacks);
      return;
    }
    const view = this.props.modelingPaper.findViewByModel(cell);
    if (view) joint.dia.HighlighterView.remove(view as any, SHOW_HIGHLIGHT);
  };

  // The running numbers of the feedback list, per element. The list assigns them and the
  // editor only draws them, so both sides never show a different number for the same entry.
  private numbersByElement: Record<string, Array<number>> = {};

  private numberBadgeMarkup = (label: string) => {
    const width = Math.max(22, 12 + 7 * label.length);
    return [
      {
        tagName: "rect",
        selector: "button",
        attributes: {
          x: -width / 2,
          y: -11,
          width,
          height: 22,
          rx: 11,
          fill: "#ff9f1c",
          stroke: "#ffffff",
          "stroke-width": 2,
          cursor: "pointer",
        },
      },
      {
        tagName: "text",
        selector: "label",
        textContent: label,
        attributes: {
          fill: "#ffffff",
          "font-size": 12,
          "font-weight": "bold",
          "font-family": "sans-serif",
          "text-anchor": "middle",
          "dominant-baseline": "central",
          "pointer-events": "none",
        },
      },
    ];
  };

  // The mark is the way back to the list: the surrounding page scrolls to the messages of
  // this element. It is a tool button, so the click never reaches the element underneath,
  // where a click means picking the element as an anchor.
  private numberBadge = (isLink: boolean, label: string, cellId: string) => {
    const action = () => window.parent.postMessage({ type: "showFeedbackFor", id: cellId }, "*");
    return isLink
      ? new joint.linkTools.Button({
          distance: "50%",
          offset: -16,
          markup: this.numberBadgeMarkup(label),
          action,
        })
      : new joint.elementTools.Button({
          useModelGeometry: true,
          x: "0%",
          y: "0%",
          offset: { x: -6, y: -6 },
          markup: this.numberBadgeMarkup(label),
          action,
        });
  };

  // One place decides what an element carries, because a view holds a single set of tools:
  // added separately, the number badge and the pick button would drop each other.
  private applyTools = (cell: joint.dia.Cell) => {
    const view = this.props.modelingPaper.findViewByModel(cell);
    if (!view) return;
    const tools: Array<joint.dia.ToolView> = [];
    const numbers = this.numbersByElement[String(cell.id)];
    if (numbers && numbers.length > 0) {
      tools.push(this.numberBadge(cell.get("type") === "kea.Link", numbers.join(", "), String(cell.id)));
    }
    if (cell.id === this.pickedCellId) tools.push(this.pickButton());
    if (tools.length === 0) (view as any).removeTools();
    else view.addTools(new joint.dia.ToolsView({ tools }));
  };

  private drawNumberBadges = () => {
    this.props.modelingGraph.getCells().forEach((cell) => this.applyTools(cell));
  };

  private showElement = (id: string) => {
    this.clearShownElement();
    if (!id) return;
    const cell = this.props.modelingGraph.getCells().find((c) => String(c.id) === id);
    if (!cell) return;
    const view = this.props.modelingPaper.findViewByModel(cell);
    if (!view) return;

    if (cell.get("type") === "kea.Link") {
      (cell as KEALink).highlight("#ff9f1c");
    } else {
      const element = cell as any;
      const selector = typeof element.getConnectionSelector === "function" ? element.getConnectionSelector() : "body";
      joint.highlighters.mask.add(view as any, selector, SHOW_HIGHLIGHT, {
        padding: 6,
        attrs: { stroke: "#ff9f1c", "stroke-width": 4 },
      });
    }
    this.shownCellId = cell.id;
  };

  // Marking the pick needs its own highlighter id, otherwise removing it would also drop
  // the assessment highlights, which use the shared "highlighter-selector" id.
  private clearPickHighlight = () => {
    if (!this.pickedCellId) return;
    const cell = this.props.modelingGraph.getCells().find((c) => c.id === this.pickedCellId);
    const view = cell ? this.props.modelingPaper.findViewByModel(cell) : undefined;
    if (view) joint.dia.HighlighterView.remove(view as any, PICK_HIGHLIGHT);
    this.pickedCellId = undefined;
    if (cell) this.applyTools(cell);
  };

  // The name lives on the model property; the label attribute is only how it is drawn and
  // is empty on shapes that render their text outside the main form, such as BPMN events.
  // getLabel is the model property; the attribute is only how the text is drawn and serves
  // as a fallback for cells that do not extend KEAElement, such as links.
  private readLabel = (element: any): string => {
    const fromModel = typeof element.getLabel === "function" ? element.getLabel() : undefined;
    const fromAttrs = typeof element.attr === "function" ? element.attr("label/text") : undefined;
    const found = [fromModel, fromAttrs].find((value) => typeof value === "string" && value.trim() !== "");
    return found ? String(found) : "";
  };

  // A button right at the element, so writing feedback for it does not mean looking for the
  // form further down the page.
  private pickButton = () =>
    new joint.elementTools.Button({
      // Without this the tool is placed against the rendered box, which for a BPMN event
      // includes the label below it and pushes the button far away from the shape.
      useModelGeometry: true,
      x: "100%",
      y: 0,
      offset: { x: 10, y: -10 },
      markup: [
        {
          tagName: "circle",
          selector: "button",
          attributes: { r: 11, fill: "#3273dc", stroke: "#ffffff", "stroke-width": 2, cursor: "pointer" },
        },
        {
          tagName: "path",
          selector: "icon",
          attributes: {
            d: "M -4 -1 L 4 -1 M 0 -5 L 0 3",
            stroke: "#ffffff",
            "stroke-width": 2,
            "stroke-linecap": "round",
            fill: "none",
            "pointer-events": "none",
          },
        },
      ],
      action: () => window.parent.postMessage({ type: "openFeedbackForPick" }, "*"),
    });

  private markPickedElement = (cellView: joint.dia.CellView) => {
    this.clearPickHighlight();
    const element = cellView.model as any;
    const selector = typeof element.getConnectionSelector === "function" ? element.getConnectionSelector() : "body";
    joint.highlighters.mask.add(cellView as any, selector, PICK_HIGHLIGHT, {
      padding: 4,
      attrs: { stroke: "#3273dc", "stroke-width": 3 },
    });
    this.pickedCellId = element.id;
    this.applyTools(cellView.model);
  };

  // Only the correction view listens for this. Clicking an element there picks it as the
  // anchor for the next manual feedback entry; clicking the empty canvas clears the pick.
  addElementPickEvents = () => {
    if (!getAuthorView()) return;

    // pointerdown, not pointerclick: the click event needs a press and release without
    // movement, so a slight wobble swallowed the pick. Focus is claimed like in the editor,
    // where an embedded iframe otherwise eats the first click.
    this.props.modelingPaper.on("element:pointerdown", (cellView) => {
      window.focus();
      const element = cellView.model;
      this.markPickedElement(cellView);
      window.parent.postMessage(
        {
          type: "elementPicked",
          id: String(element.id),
          label: this.readLabel(element),
          elementType: String(element.attributes?.type ?? ""),
          eventType: String((element as any).get?.("eventType") ?? ""),
        },
        "*",
      );
    });

    this.props.modelingPaper.on("blank:pointerclick", () => {
      this.clearPickHighlight();
      window.parent.postMessage({ type: "elementPicked", id: "", label: "" }, "*");
    });

  };

  // Registered in every view, not only while correcting: the result view also hands over
  // the running numbers and expects them to be drawn.
  addMessageEvents = () => {
    window.addEventListener("message", (event) => {
      // The surrounding page can drop the pick, e.g. via the button in the feedback dialog.
      if (event.data?.type === "clearElementPick") this.clearPickHighlight();
      if (event.data?.type === "feedbackChanged") {
        const id = getSubmissionExerciseId();
        if (id) this.loadFeedback(id, 0);
      }
      if (event.data?.type === "showNumbers") {
        this.numbersByElement = event.data.numbers ?? {};
        this.drawNumberBadges();
      }
      // Clicking a message in the list points at the element it belongs to. Its own
      // highlighter id keeps it apart from the assessment marks and from the anchor pick.
      if (event.data?.type === "showElement") this.showElement(String(event.data.id ?? ""));
    });
  };

  addMouseEnterAndMouseLeaveEvents = () => {
    this.props.modelingPaper.on("cell:mouseenter", (cellView) => {
      this.props.setNode(cellView.model);
    });

    this.props.modelingPaper.on("cell:mouseleave", () => {
      this.props.setNode();
    });
  };

  unhighlightAllElements = () => {
    const cells = this.props.modelingGraph.getCells();
    cells.forEach((cell) => {
      if (cell.get("type") === "kea.Link") {
        (cell as KEALink).unhighlight();
      } else {
        const cellView = this.props.modelingPaper.findViewByModel(cell);
        const elementView: joint.dia.ElementView = cellView as unknown as joint.dia.ElementView;
        joint.dia.HighlighterView.remove(elementView);
      }
    });
  };

  render() {
    return (
      <>
        <div style={{ position: "relative", width: this.props.width, height: this.props.height }}>
          <FeedbackGraph setNode={this.props.setNode} width={this.props.width} height={this.props.height} />
          {!this.props.keaGraphContext.modelLoaded && <ModelLoadingOverlay />}
        </div>
        <ButtonGroup
          alignHorizontal={Horizontal.RIGHT}
          alignVertical={Vertical.BOTTOM}
          verticalSpace="1rem"
          horizontalSpace="1rem"
        >
          <ZoomInButton />
          <ZoomOutButton />
          <FitModelButton />
          <ModelExportButton />
        </ButtonGroup>
      </>
    );
  }
}
